import { getAnswerByCode } from '../utils/onboarding';
import type { OnboardingSummary } from '../types/onboarding';
import type { Content } from '../types/uploader';

const useOnboardingSummary = ({ section }: { section: OnboardingSummary }) => {
  if (section.section_code === 'identity') {
    const age = getAnswerByCode('age', section.questions) as string;
    const gender = getAnswerByCode('gender', section.questions) as string;
    const resume = (
      getAnswerByCode('resume_linkedin_bio', section.questions) as Record<
        string,
        Content
      >
    )?.resume;
    const linkedin = (
      getAnswerByCode('resume_linkedin_bio', section.questions) as Record<
        string,
        string
      >
    )?.linkedin;
    const briefProfessionalBackground = (
      getAnswerByCode('resume_linkedin_bio', section.questions) as Record<
        string,
        string
      >
    )?.bio;
    const elevatorPitch = getAnswerByCode(
      'elevator_pitch',
      section.questions
    ) as string;
    const coreStrengths = getAnswerByCode(
      'core_strength_1',
      section.questions
    ) as string;
    const coreStrengths2 = getAnswerByCode(
      'core_strength_2',
      section.questions
    ) as string;
    const coreStrengths3 = getAnswerByCode(
      'core_strength_3',
      section.questions
    ) as string;

    return {
      age,
      gender,
      resume,
      linkedin,
      briefProfessionalBackground,
      elevatorPitch,
      coreStrengths,
      coreStrengths2,
      coreStrengths3,
    };
  }

  if (section.section_code === 'core_objectives') {
    const primaryGoal = getAnswerByCode(
      'primary_goal',
      section.questions
    ) as string;

    if (primaryGoal === 'Raising capital for my start-up') {
      const fundingStage = getAnswerByCode(
        'funding_stage',
        section.questions
      ) as string;
      const targetCheckSize = getAnswerByCode(
        'target_check_size',
        section.questions
      ) as string;
      const keyMetrics = getAnswerByCode(
        'key_metrics',
        section.questions
      ) as string;

      return {
        fundingStage,
        targetCheckSize,
        keyMetrics,
      };
    }

    if (
      primaryGoal === 'Finding investment opportunities (as a VC, angel, etc.)'
    ) {
      let stagePreference = getAnswerByCode(
        'stage_preference',
        section.questions
      );
      let industryFocusAreas = getAnswerByCode(
        'industry_focus_areas',
        section.questions
      );

      if (stagePreference && typeof stagePreference === 'string') {
        stagePreference = stagePreference.split(',') as string[];
      }
      if (industryFocusAreas && typeof industryFocusAreas === 'string') {
        industryFocusAreas = industryFocusAreas.split(',') as string[];
      }

      return {
        stagePreference,
        industryFocusAreas,
      };
    }

    if (primaryGoal === 'Generating qualified sales leads for my business') {
      const idealCustomerProfile = getAnswerByCode(
        'ideal_customer_profile',
        section.questions
      ) as string;
      const buyingSignals = getAnswerByCode(
        'buying_signals',
        section.questions
      ) as string;

      return {
        idealCustomerProfile,
        buyingSignals,
      };
    }

    if (primaryGoal === 'Finding a mentor or career advisor') {
      const skillsToLearn = getAnswerByCode(
        'skills_to_learn',
        section.questions
      ) as string;
      const careerHurdles = getAnswerByCode(
        'career_hurdles',
        section.questions
      ) as string;

      return {
        skillsToLearn,
        careerHurdles,
      };
    }

    if (primaryGoal === 'Offering mentorship to others') {
      const mentorshipAreas = getAnswerByCode(
        'mentorship_areas',
        section.questions
      ) as string;
      const idealMentees = getAnswerByCode(
        'ideal_mentees',
        section.questions
      ) as string;

      return {
        mentorshipAreas,
        idealMentees,
      };
    }

    if (primaryGoal === 'Exploring strategic partnerships or collaborations') {
      const idealPartnerProfile = getAnswerByCode(
        'ideal_partner_profile',
        section.questions
      ) as string;
      const partnershipType = getAnswerByCode(
        'partnership_type',
        section.questions
      ) as string;

      return {
        idealPartnerProfile,
        partnershipType,
      };
    }

    if (primaryGoal === 'Recruiting for a key role') {
      const roleTitleResponsibilities = getAnswerByCode(
        'role_title_responsibilities',
        section.questions
      ) as string;
      const industriesOfInterest = getAnswerByCode(
        'key_qualifications',
        section.questions
      ) as string;
      const cultureValues = getAnswerByCode(
        'culture_values',
        section.questions
      ) as string;
      return {
        roleTitleResponsibilities,
        industriesOfInterest,
        cultureValues,
      };
    }

    if (primaryGoal === 'General learning and professional development') {
      const topicsExploring = getAnswerByCode(
        'topics_exploring',
        section.questions
      ) as string;
      const learningStyle = getAnswerByCode(
        'learning_style',
        section.questions
      ) as string;
      return {
        topicsExploring,
        learningStyle,
      };
    }

    return {};
  }

  if (section.section_code === 'ideal_connections') {
    const preferredRoles = getAnswerByCode(
      'preferred_roles_titles',
      section.questions
    ) as string;
    const industriesOfInterest = getAnswerByCode(
      'industries_of_interest',
      section.questions
    ) as string;
    const connectionFundingStage = getAnswerByCode(
      'connection_funding_stage',
      section.questions
    ) as string;
    const dealBreakers = getAnswerByCode(
      'deal_breakers',
      section.questions
    ) as string;

    return {
      preferredRoles,
      industriesOfInterest,
      connectionFundingStage,
      dealBreakers,
    };
  }

  if (section.section_code === 'style_prefs') {
    const introductionPacing = getAnswerByCode(
      'introduction_pacing',
      section.questions
    ) as string;
    const communicationStyle = getAnswerByCode(
      'communication_style',
      section.questions
    ) as string;
    const preferredIntroductionMethod = getAnswerByCode(
      'preferred_introduction_method',
      section.questions
    ) as string;

    return {
      introductionPacing,
      communicationStyle,
      preferredIntroductionMethod,
    };
  }

  if (section.section_code === 'extra') {
    const extraInfo = getAnswerByCode(
      'anything_else',
      section.questions
    ) as string;

    return {
      extraInfo,
    };
  }

  return {};
};

export default useOnboardingSummary;
